import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useRevokeTeenAccess,
  useTeenAccessRecords,
} from "@/lib/api/teen-access";
import { useUser } from "@/lib/api/user";
import {
  EllipsisVerticalIcon,
  PlusIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { ConfirmActionDialog } from "../ui/confirm-action-dialog";
import { useToast } from "../ui/use-toast";
import { AddAccessRecordDialog } from "./add-access-record-popover";
import { EditPermissionsPopover } from "./edit-permissions-popover";

interface TeenAccessRecordsProps {
  menteeId: string;
}

interface ParentConsentRejectedProps {
  reason: string;
}

function ParentConsentRejected({ reason }: ParentConsentRejectedProps) {
  return (
    <div className="mt-1 flex items-center gap-2">
      <XCircleIcon className="h-7 w-7 text-gray-500" />
      <div>
        <h3 className="text-sm font-semibold text-black">
          Parent Consent Rejected
        </h3>
        <p className="text-sm text-gray-500">Reason - &quot;{reason}&quot;</p>
      </div>
    </div>
  );
}

export function TeenAccessRecords({ menteeId }: TeenAccessRecordsProps) {
  const { data: mentee } = useUser(menteeId);
  const { data: records, isLoading } = useTeenAccessRecords(menteeId);
  const { mutateAsync: revokeAccess, isPending: isRevoking } =
    useRevokeTeenAccess(menteeId);

  const [editingRecord, setEditingRecord] = useState<string | null>(null);
  const [addingRecord, setAddingRecord] = useState(false);
  const [revokeRecord, setRevokeRecord] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [popoverPosition, setPopoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const { toast } = useToast();

  const handleEditClick = (recordId: string, event: React.MouseEvent) => {
    setEditingRecord(recordId);
    setPopoverPosition({
      x: event.clientX - 16,
      y: event.clientY + 8,
    });
  };

  const handleAddClick = () => {
    setAddingRecord(true);
  };

  const handlePopoverClose = () => {
    setEditingRecord(null);
    setAddingRecord(false);
    setPopoverPosition(null);
  };

  const handleRevokeAccess = async () => {
    if (!revokeRecord) return;

    try {
      await revokeAccess(revokeRecord.id);
      toast({
        title: "Successfully revoked access",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to revoke access",
        variant: "destructive",
      });
    } finally {
      setRevokeRecord(null);
    }
  };

  const hasRejectedParentConsent =
    mentee?.profile?.metadata?.parentConsent &&
    mentee?.profile?.metadata?.parentConsent !== "Yes";

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between border-b py-4">
        <h2 className="text-lg font-bold leading-snug text-black">
          Approved Users
        </h2>
        <Button
          size="sm"
          onClick={handleAddClick}
          className="flex items-center space-x-1"
        >
          <PlusIcon className="h-4 w-4" />
          <span>Add New</span>
        </Button>
      </div>

      <div className="mt-2">
        {!hasRejectedParentConsent && (
          <ParentConsentRejected
            reason={mentee?.profile?.metadata?.parentOptOutReason || ""}
          />
        )}
        {isLoading ? (
          <div className="mt-4 h-48 animate-pulse rounded-lg bg-gray-200" />
        ) : !records?.length ? (
          <p className="text-md mt-4 flex h-20 items-center justify-center font-normal leading-tight text-[#777777]">
            No approved users
          </p>
        ) : (
          records?.map((record) => (
            <div
              key={record.id}
              className="flex items-center justify-between py-2"
            >
              <div>
                <h3 className="text-base font-bold leading-tight text-black">
                  {record.granted_to_user.name}
                </h3>
                <p className="font-twk my-1 text-base font-normal leading-tight text-[#ababab]">
                  {record.granted_to_user.phone}
                </p>
                <div className="mt-1">
                  {record.types.map((type) => (
                    <span
                      key={type}
                      className="mr-2 inline-flex items-center rounded-full border border-[#c7c7c7] bg-[#f1f1f1] px-3 py-1 text-center text-xs font-normal leading-none text-[#777777]"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100">
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={(e) => {
                        handleEditClick(record.id, e);
                      }}
                    >
                      Edit Permissions
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => {
                        setRevokeRecord({
                          id: record.granted_to_user.id,
                          name: record.granted_to_user.name,
                        });
                      }}
                    >
                      Revoke Access
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <EditPermissionsPopover
                  record={record}
                  menteeId={menteeId}
                  open={editingRecord === record.id}
                  onOpenChange={(open) => {
                    if (!open) handlePopoverClose();
                  }}
                  anchorPosition={popoverPosition || undefined}
                />
              </div>
            </div>
          ))
        )}
      </div>

      <AddAccessRecordDialog
        menteeId={menteeId}
        open={addingRecord}
        onOpenChange={(open) => {
          if (!open) handlePopoverClose();
        }}
      />

      <ConfirmActionDialog
        isOpen={!!revokeRecord}
        onClose={() => setRevokeRecord(null)}
        onConfirm={handleRevokeAccess}
        title={`Revoke Access for ${revokeRecord?.name}`}
        description="This action will remove all permissions and access for this user. They will no longer be able to view any information about this teen."
        loading={isRevoking}
      />
    </div>
  );
}
